import { AsyncPipe } from "@angular/common";
import { Component, DestroyRef, effect, inject, input, OnInit } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { FormBuilder, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { UiKitModule } from "@app/ui-kit.module";
import { FloorplanItem } from "@domain/project/floorplan/floorplan-item";
import { DangerArea } from "@domain/project/floorplan/zones/danger-area";
import { DustExZone } from "@domain/project/floorplan/zones/dust-ex-zone";
import { ExZone } from "@domain/project/floorplan/zones/ex-zone";
import { IsolationRoom } from "@domain/project/floorplan/zones/isolation-room";
import { ItRoom } from "@domain/project/floorplan/zones/it-room";
import { OperatingTheatre } from "@domain/project/floorplan/zones/operating-theatre";
import { ProcedureRoom } from "@domain/project/floorplan/zones/procedure-room";
import { StandardRoom } from "@domain/project/floorplan/zones/standard-room";
import { WildcardZone } from "@domain/project/floorplan/zones/wildcard-zone";
import { ProjectService } from "@domain/project/project.service";
import { UserService } from "@domain/user/user.service";
import { FormDirective, FormFieldComponent, FormFieldControlDirective } from "@odx/angular/components/form-field";
import { TooltipDirective } from "@odx/angular/components/tooltip";
import { FloorplanItemDetailsComponent } from "@project/floorplanner/floorplan-additional-notes/floorplan-item-details/floorplan-item-details.component";
import { FloorplanService } from "@project/floorplanner/floorplan.service";
import { filter, map, Observable, Subject, takeUntil, withLatestFrom } from "rxjs";

@Component({
  selector: "app-zone-notes",
  imports: [
    FormDirective,
    FormFieldComponent,
    FloorplanItemDetailsComponent,
    ReactiveFormsModule,
    FormFieldControlDirective,
    AsyncPipe,
    TooltipDirective,
    UiKitModule,
  ],
  templateUrl: "./zone-notes.component.html",
  styleUrl: "./zone-notes.component.scss",
  standalone: true,
})
export class ZoneNotesComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly projectService = inject(ProjectService);
  private readonly floorplanService = inject(FloorplanService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  readonly floorplanItem = input.required<FloorplanItem>();

  protected zone!:
    DangerArea | ExZone | DustExZone | WildcardZone | IsolationRoom | ItRoom | OperatingTheatre | ProcedureRoom | StandardRoom;
  protected zoneSizeForm?: FormGroup;
  protected isReadOnlyUser$: Observable<boolean>;
  protected hasWritePermission$: Observable<boolean | undefined>;
  protected isReferenceMeasureSet$!: Observable<boolean>;

  protected readonly tooltipTextNoReferenceMeasure: string = $localize`:@@floorplan.referenceMeasure.noReferenceMeasureIsSet:Ein Referenzmaß ist für Änderungen erforderlich`;

  constructor() {
    this.isReadOnlyUser$ = this.userService.user$.pipe(map((user) => user.readOnlyAccess));
    this.hasWritePermission$ = this.projectService.hasUserWritePermission$.pipe(
      filter((val): val is boolean => val !== undefined),
    );
    effect(() => {
      const item = this.floorplanItem();
      if (item instanceof DangerArea) this.zone = item as DangerArea;
      else if (item instanceof ExZone) this.zone = item as ExZone;
      else if (item instanceof DustExZone) this.zone = item as DustExZone;
      else if (item instanceof WildcardZone) this.zone = item as WildcardZone;
      else if (item instanceof IsolationRoom) this.zone = item as IsolationRoom;
      else if (item instanceof ItRoom) this.zone = item as ItRoom;
      else if (item instanceof OperatingTheatre) this.zone = item as OperatingTheatre;
      else if (item instanceof ProcedureRoom) this.zone = item as ProcedureRoom;
      else if (item instanceof StandardRoom) this.zone = item as StandardRoom;

      const cleanUp$ = new Subject<void>();
      this.setupZoneDimensionForm(cleanUp$);
      return () => {
        cleanUp$.next();
        cleanUp$.complete();
      };
    });
  }

  ngOnInit(): void {
    this.isReferenceMeasureSet$ = this.floorplanService.isReferenceMeasureSet$;
  }

  private setupZoneDimensionForm(destroy$: Subject<void>) {
    if (!this.zoneSizeForm) {
      this.initZoneDimensionForm();
    } else {
      this.zoneSizeForm.reset(
        { referenceWidth: this.zone.referenceWidth, referenceHeight: this.zone.referenceHeight },
        { emitEvent: false },
      );
    }

    this.zoneSizeForm!.valueChanges.pipe(
      withLatestFrom(this.isReferenceMeasureSet$),
      takeUntil(destroy$),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(([values, isReferenceMeasureSet]) => {
      if (!isReferenceMeasureSet) {
        return;
      }
      if (this.hasReferenceSizeChanged(values.referenceWidth, values.referenceHeight)) {
        if (values.referenceWidth && values.referenceHeight) this.zone.updateSize(values.referenceWidth, values.referenceHeight);
      }
    });

    this.zone.sizeChanged.pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef)).subscribe(({ width, height }) => {
      this.zone.updateReferenceSize(width, height);
      this.patchSizeValues();
    });

    this.zone.referenceSizeChanged.pipe(takeUntil(destroy$), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.patchSizeValues();
    });
  }

  private initZoneDimensionForm() {
    this.zone.sizeChanged = new Subject();
    this.zoneSizeForm = this.formBuilder.group({
      referenceWidth: [this.zone.referenceWidth],
      referenceHeight: [this.zone.referenceHeight],
    });
  }

  private patchSizeValues() {
    this.zoneSizeForm!.patchValue(
      { referenceWidth: this.zone.referenceWidth, referenceHeight: this.zone.referenceHeight },
      { emitEvent: false },
    );
  }

  private hasReferenceSizeChanged(referenceWidth: number | undefined, referenceHeight: number | undefined) {
    return !(referenceWidth === this.zone.referenceWidth && referenceHeight === this.zone.referenceHeight);
  }
}
